import { useState } from 'react';
import { cleanId } from '../shared/clean-id.ts';
import { api, unwrap } from './api.ts';

type Attachment = {
  id: string;
  name: string;
  file: File;
  asset?: { id: string; fileName: string; byteSize: number };
  status: 'queued' | 'uploading' | 'ready' | 'failed';
  error?: unknown;
};
export function useChatAttachments() {
  const [items, setItems] = useState<Attachment[]>([]);
  const [error, setError] = useState<unknown>();
  const update = (id: string, change: Partial<Attachment>) =>
    setItems((items) => items.map((item) => (item.id === id ? { ...item, ...change } : item)));
  const uploading = items.some((item) => item.status === 'uploading');
  const tooLarge = items.reduce((sum, item) => sum + (item.asset?.byteSize ?? 0), 0) > 40 * 1024 * 1024;
  return {
    items,
    ids: items.map((item) => item.id),
    uploading,
    blocked: uploading || tooLarge,
    error: tooLarge ? new Error('Keep the combined attachments in one message below 40 MiB.') : error,
    upload: async (chatId: string, ids: string[]) => {
      const results = await Promise.allSettled(
        items
          .filter((item) => ids.includes(item.id))
          .map(async (item) => {
            if (item.asset) return item.asset;
            update(item.id, { status: 'uploading', error: undefined });
            try {
              const asset = unwrap(
                await api.PUT('/api/chats/{chatId}/attachments/{attachmentId}/bytes', {
                  params: { path: { chatId, attachmentId: item.id }, query: { fileName: item.file.name } },
                  headers: { 'Content-Type': 'application/octet-stream' },
                  body: item.file,
                  bodySerializer: (file) => file,
                }),
              );
              update(item.id, { status: 'ready', name: asset.fileName, asset });
              return asset;
            } catch (error) {
              update(item.id, { status: 'failed', error });
              throw error;
            }
          }),
      );
      const failure = results.find((result) => result.status === 'rejected');
      if (failure) throw failure.reason;
      const byteSize = results.reduce(
        (sum, result) => sum + (result.status === 'fulfilled' ? result.value.byteSize : 0),
        0,
      );
      if (byteSize > 40 * 1024 * 1024)
        throw new Error('Keep the combined attachments in one message below 40 MiB.');
    },
    add: (files: File[]) => {
      if (files.length + items.length > 20) {
        setError(new Error('Attach up to 20 files in one message.'));
        return;
      }
      setError(undefined);
      const added: Attachment[] = files.map((file) => ({
        id: cleanId(),
        name: file.name,
        file,
        status: 'queued',
      }));
      setItems((items) => [...items, ...added]);
    },
    remove: (id: string) => setItems((items) => items.filter((item) => item.id !== id)),
    clearSent: (ids: string[]) => setItems((items) => items.filter((item) => !ids.includes(item.id))),
  };
}
export type AttachmentQueue = ReturnType<typeof useChatAttachments>;
